/**
 * Performance métier réseau (SuperAdmin, brique E) — LOGIQUE PURE.
 *
 * Comparatif des restos sur une fenêtre glissante : commandes, CA, ticket moyen,
 * % paiement en ligne. Aucune I/O ici (cf. performanceService.ts).
 * Données via RPC `network_performance` (counts bruts → ratios calculés ici).
 */

/** Stats brutes d'un resto renvoyées par la RPC. */
interface RawPerf {
  tenant_id?: string;
  name?: string | null;
  slug?: string | null;
  orders?: number | null;
  revenue_cents?: number | null;
  online_orders?: number | null;
}

/** Stats enrichies d'un resto (ratios calculés). */
export interface TenantPerf {
  tenantId: string;
  name: string;
  slug: string;
  orders: number;
  revenueCents: number;
  onlineOrders: number;
  /** Ticket moyen en centimes (0 si aucune commande). */
  avgTicketCents: number;
  /** Part de commandes payées en ligne, 0..1 (0 si aucune commande). */
  onlineRatio: number;
}

export interface NetworkPerformance {
  windowDays: number;
  tenants: TenantPerf[];
}

/** Ticket moyen sûr (évite la division par zéro). */
export function avgTicket(revenueCents: number, orders: number): number {
  return orders > 0 ? Math.round(revenueCents / orders) : 0;
}

/** Ratio en ligne sûr (0..1). */
export function onlineRatio(onlineOrders: number, orders: number): number {
  return orders > 0 ? onlineOrders / orders : 0;
}

function toTenantPerf(raw: RawPerf): TenantPerf {
  const orders = raw.orders ?? 0;
  const revenueCents = raw.revenue_cents ?? 0;
  const onlineOrders = raw.online_orders ?? 0;
  return {
    tenantId: raw.tenant_id ?? '',
    name: raw.name ?? '',
    slug: raw.slug ?? '',
    orders,
    revenueCents,
    onlineOrders,
    avgTicketCents: avgTicket(revenueCents, orders),
    onlineRatio: onlineRatio(onlineOrders, orders),
  };
}

/** Parse la réponse jsonb de la RPC. */
export function parsePerformance(data: unknown): NetworkPerformance {
  const obj = (data ?? {}) as { window_days?: number; tenants?: RawPerf[] };
  return {
    windowDays: obj.window_days ?? 30,
    tenants: Array.isArray(obj.tenants) ? obj.tenants.map(toTenantPerf) : [],
  };
}

/** Totaux et moyennes réseau (agrégés sur tous les restos). */
export function networkTotals(tenants: TenantPerf[]): {
  orders: number;
  revenueCents: number;
  avgTicketCents: number;
  onlineRatio: number;
} {
  const orders = tenants.reduce((s, t) => s + t.orders, 0);
  const revenueCents = tenants.reduce((s, t) => s + t.revenueCents, 0);
  const onlineOrders = tenants.reduce((s, t) => s + t.onlineOrders, 0);
  return {
    orders,
    revenueCents,
    avgTicketCents: avgTicket(revenueCents, orders),
    onlineRatio: onlineRatio(onlineOrders, orders),
  };
}

/** Formatage € à partir de centimes (ex. 1234 → "12,34 €"). */
export function euros(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** Formatage pourcentage à partir d'un ratio 0..1 (ex. 0.5 → "50 %"). */
export function percent(ratio: number): string {
  return Math.round(ratio * 100) + ' %';
}
